import { Component, DestroyRef, effect, inject, OnInit, WritableSignal } from '@angular/core';
import { DashboardType, TopBarWrapperComponent } from '../top-bar-wrapper/top-bar-wrapper.component';
import { AiPresenceAdvertisingImpactPayload, AiPresenceAdvertisingImpactResponse, AiPresenceOverviewPayload, AiPresenceOverviewResponse, AiPresenceShareOfVoicePayload, AiPresenceShareOfVoiceResponse, AiPresenceTopicsTablePayload, AiPresenceTopicsTableResponse, ShareOfVoiceTriggerSource } from './types';
import { DashboardFilters, DashboardService } from '../../services/dashboard.service';
import { AiPresenceService } from '../../services/ai-presence.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, finalize, catchError, of, Subject, map } from 'rxjs';
import { DateRangeValue } from '../../utils/time';
import { SelectItem, SelectMode } from '../../shared/components/select-menu/select-menu.component';
import { FormControl } from '@angular/forms';
import { UtilService } from '../../services/util.service';
import { OverviewTilesComponent } from '../../shared/components/overview-tiles/overview-tiles.component';
import { WidgetContainerComponent } from '../my-brand-screen/widget-container/widget-container.component';
import { AdvertisingImpactComponent } from './advertising-impact/advertising-impact.component';
import { TopicsTableComponent } from './topics-table/topics-table.component';
import { tooltips } from '../../utils/tooltips';
import { AiShareOfVoiceComponent } from './ai-share-of-voice/ai-share-of-voice.component';
import { StateService } from '../../services/state.service';

@Component({
  selector: 'app-ai-presence-screen',
  imports: [
    TopBarWrapperComponent,
    OverviewTilesComponent,
    WidgetContainerComponent,
    AdvertisingImpactComponent,
    TopicsTableComponent,
    AiShareOfVoiceComponent
],
  templateUrl: './ai-presence-screen.component.html',
  styleUrl: './ai-presence-screen.component.scss'
})
export class AiPresenceScreenComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  private aiPresenceService = inject(AiPresenceService);
  private destroyRef = inject(DestroyRef);
  private utilService = inject(UtilService);
  private stateService = inject(StateService);

  protected readonly tooltips = tooltips.aiPresence;
  protected readonly DashboardType = DashboardType;
  protected readonly SelectMode = SelectMode;
  protected readonly dashboardFilters: WritableSignal<DashboardFilters> = this.dashboardService.brandsFilters;

  protected loadingState = {
    overview: true,
    advertisingImpact: true,
    topicsTable: true,
    shareOfVoice: true
  }

  // widget data subjects
  private overviewData$ = new Subject<AiPresenceOverviewPayload>();
  private advertisingImpactData$ = new Subject<AiPresenceAdvertisingImpactPayload>();
  private topicsTableData$ = new Subject<AiPresenceTopicsTablePayload>();
  private shareOfVoiceData$ = new Subject<[AiPresenceShareOfVoicePayload, ShareOfVoiceTriggerSource]>();

  // widget data
  protected overviewData: AiPresenceOverviewResponse | undefined;
  protected advertisingImpactData: AiPresenceAdvertisingImpactResponse | undefined;
  protected topicsTableData: AiPresenceTopicsTableResponse | undefined;
  protected shareOfVoiceData: AiPresenceShareOfVoiceResponse | undefined;

  protected shareOfVoiceTopicsOptions = {
    metricOptions: [] as SelectItem[],
    selectedOption: null as SelectItem | null,
    metricControl: new FormControl(null) as FormControl<string | null>,
    searchable: false
  }

  constructor() {
    effect(() => {
      const dashboardFilters = this.dashboardService.brandsFilters();
      this.initializeData(dashboardFilters);
    });
  }

  ngOnInit() {
    this.initSubjects();
  }

  private initializeData(dashboardFilters: DashboardFilters): void {
    const brandKey = dashboardFilters.userRelatedBrand.key;
    const dateRange = dashboardFilters.dateRange;

    this.overviewData$.next(this.getOverviewPayload(brandKey, dateRange));
    this.advertisingImpactData$.next(this.getAdvertisingImpactPayload(brandKey, dateRange));
    this.topicsTableData$.next(this.getTopicsTablePayload(brandKey, dateRange));
    this.shareOfVoiceData$.next([this.getShareOfVoicePayload(brandKey, dateRange), ShareOfVoiceTriggerSource.INITIAL_LOAD]);

    this.setShareOfVoiceTopicsOptions();
  }

  private getOverviewPayload(brandKey: string, dateRange: DateRangeValue): AiPresenceOverviewPayload {
    return {
      ...dateRange,
      brandKey: brandKey,
      accountKey: this.stateService.activeAccount()
    };
  }

  private getAdvertisingImpactPayload(brandKey: string, dateRange: DateRangeValue): AiPresenceAdvertisingImpactPayload {
    return {
      ...dateRange,
      brandKey: brandKey,
      accountKey: this.stateService.activeAccount()
    };
  }

  private getTopicsTablePayload(brandKey: string, dateRange: DateRangeValue): AiPresenceTopicsTablePayload {
    return {
      ...dateRange,
      brandKey: brandKey,
      accountKey: this.stateService.activeAccount()
    };
  }

  private getShareOfVoicePayload(brandKey: string, dateRange: DateRangeValue, topic?: string): AiPresenceShareOfVoicePayload {
    return {
      ...dateRange,
      brandKey: brandKey,
      accountKey: this.stateService.activeAccount(),
      ...(topic ? { topic } : {})
    };
  }

  private initSubjects() {
    this.initOverviewSubject();
    this.initAdvertisingImpactSubject();
    this.initTopicsTableSubject();
    this.initShareOfVoiceSubject();
  }

  private initOverviewSubject(): void {
    this.overviewData$
      .pipe(
        switchMap((payload: AiPresenceOverviewPayload) => {
          this.loadingState.overview = true;
          return this.aiPresenceService.getOverview(payload)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => {
                this.loadingState.overview = false;
              }),
              catchError(
                () => {
                  this.overviewData = undefined;
                  return of(undefined)
                }
              )
            );
        })
      )
      .subscribe(result => {
        this.overviewData = result;
      });
  }

  private initAdvertisingImpactSubject(): void {
    this.advertisingImpactData$
      .pipe(
        switchMap((payload: AiPresenceAdvertisingImpactPayload) => {
          this.loadingState.advertisingImpact = true;
          return this.aiPresenceService.getAdvertisingImpact(payload)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => {
                this.loadingState.advertisingImpact = false;
              }),
              catchError(
                () => {
                  this.advertisingImpactData = undefined;
                  return of(undefined)
                }
              )
            );
        })
      )
      .subscribe(result => {
        this.advertisingImpactData = result;
      });
  }

  private initTopicsTableSubject(): void {
    this.topicsTableData$
      .pipe(
        switchMap((payload: AiPresenceTopicsTablePayload) => {
          this.loadingState.topicsTable = true;
          return this.aiPresenceService.getTopicsTable(payload)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => {
                this.loadingState.topicsTable = false;
              }),
              catchError(
                () => {
                  this.topicsTableData = undefined;
                  return of(undefined)
                }
              )
            );
        })
      )
      .subscribe(result => {
        this.topicsTableData = result;
      });
  }

  private initShareOfVoiceSubject(): void {
    this.shareOfVoiceData$
      .pipe(
        switchMap(([payload, triggerSource]: [AiPresenceShareOfVoicePayload, ShareOfVoiceTriggerSource]) => {
          this.loadingState.shareOfVoice = true;
          return this.aiPresenceService.getShareOfVoice(payload)
            .pipe(
              takeUntilDestroyed(this.destroyRef),
              finalize(() => {
                this.loadingState.shareOfVoice = false;
              }),
              map(result => ({ result, triggerSource })),
              catchError(
                () => {
                  this.shareOfVoiceData = undefined;
                  return of(undefined)
                }
              )
            );
        })
      )
      .subscribe((params) => {
        const { result, triggerSource } = params || {};
        this.shareOfVoiceData = result;
        this.setShareOfVoiceTopicsOptions(triggerSource);
      });
  }

  private setShareOfVoiceTopicsOptions(triggerSource?: ShareOfVoiceTriggerSource): void {
    if (triggerSource === ShareOfVoiceTriggerSource.TOPIC_CHANGE) {
      return;
    }

    if (!this.shareOfVoiceData) {
      // reset options
      this.shareOfVoiceTopicsOptions.metricOptions = [];
      this.shareOfVoiceTopicsOptions.selectedOption = null;
      this.shareOfVoiceTopicsOptions.metricControl = new FormControl(null);
      this.shareOfVoiceTopicsOptions.searchable = false;
      return;
    }

    const topics = ['all topics', ...this.shareOfVoiceData.topics];
    const options: SelectItem[] = topics.map(topicData => ({
      key: topicData,
      label: this.utilService.capitalizeFirstLetter(topicData)
    }));

    this.shareOfVoiceTopicsOptions.metricOptions = options;
    this.shareOfVoiceTopicsOptions.selectedOption = options[0];
    this.shareOfVoiceTopicsOptions.metricControl = new FormControl(options[0].key);
    this.shareOfVoiceTopicsOptions.searchable = !!(options.length > 7);
  }

  protected onShareOfVoiceTopicChange(): void {
    const { metricControl } = this.shareOfVoiceTopicsOptions;
    if (!metricControl || !metricControl.value) {
      return;
    }

    this.loadingState.shareOfVoice = true;

    const dashboardFilters = this.dashboardFilters();
    const brandKey = dashboardFilters.userRelatedBrand.key;
    const dateRange = dashboardFilters.dateRange;
    const topic = metricControl.value === 'all topics' ? undefined : metricControl.value;
    const payload = this.getShareOfVoicePayload(brandKey, dateRange, topic);

    this.shareOfVoiceData$.next([payload, ShareOfVoiceTriggerSource.TOPIC_CHANGE]);
  }

}
